import React, { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { pageMotion } from "../motion/pageMotion";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { blockchainService } from "../../services/blockchainService";
import { useMemo } from "react";
import Swal from "sweetalert2";
import {
  TabContainer,
  TabsScrollWrapper,
  LinearTabs,
  Tab,
  Content,
  WarningContainer,
} from "./styles";
import {
  ButtonContainer,
  InitializeButton,
  StyledButton,
} from "../../pages/project_intern/intel/styles.js";
import { projectService } from "../../services/projectService.js";
import { WarningCard } from "../projects_t_engineering/FileUpload/WarningCard/WarningCard.jsx";
import { AdministrativoCard } from "../projects_t_engineering/FileUpload/AdministrativoCard/AdministrativoCard.jsx";
import { getVisibleProjectTabLabels } from "../../utils/userRoles";

function formatApiError(error) {
  const data = error?.response?.data;
  if (!data) return error?.message || "Erro desconhecido";
  if (typeof data === "string") return data;
  if (typeof data.detail === "string") return data.detail;
  if (typeof data.error === "string") return data.error;
  try {
    return Object.entries(data)
      .map(([key, val]) => {
        const msg = Array.isArray(val) ? val.join(", ") : String(val);
        return `${key}: ${msg}`;
      })
      .join(" | ");
  } catch {
    return "Erro na API";
  }
}

const ProjectTabs = ({ tabs, handleRegister, project, onProjectUpdated }) => {
  const navigate = useNavigate();
  const currentUser = useSelector((state) => state.user.currentUser);
  const collapsed = useSelector((state) => state.sidebar);
  const token = sessionStorage.getItem("Authorization");

  const headers = useMemo(
    () => ({
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    }),
    [token]
  );

  const visibleTabs = useMemo(() => {
    const labels = getVisibleProjectTabLabels(currentUser);
    return (tabs || []).filter((tab) => labels.includes(tab.label));
  }, [tabs, currentUser]);

  const [isDocConfirmed, setDocConfirmed] = useState(false);
  // 🔥 Estado local do projeto
  const [localProject, setLocalProject] = useState(project);
  const [activeTab, setActiveTab] = useState(0);

  const activeLabel = visibleTabs[activeTab]?.label;

  const showAdministrativoWarning =
    activeLabel === "Administrativo" && !isDocConfirmed;

  const showWarning =
    (activeLabel === "Comercial" || activeLabel === "Jurídico") &&
    !isDocConfirmed;

  // 🔄 Mantém sincronizado se o projeto mudar via props
  useEffect(() => {
    setLocalProject(project);

    if (project?.id) {
      verificaDocsConfirmados();
    }
  }, [project]);

  const status = localProject?.status;
  const isNotStarted = status == null || status === "";
  const tabStatus = isNotStarted ? null : status;

  useEffect(() => {
    if (isNotStarted) {
      setActiveTab(0);
    }
  }, [isNotStarted]);

  // Se a lista de abas mudar (ex.: ENG só Informações + Ambiental), mantém índice válido
  useEffect(() => {
    if (activeTab >= visibleTabs.length) {
      setActiveTab(0);
    }
  }, [visibleTabs.length, activeTab]);

  const handleTabClick = (index) => {
    if (
      !isNotStarted ||
      index === 0 ||
      visibleTabs[index]?.label === "Informações"
    ) {
      setActiveTab(index);
    } else if (!isNotStarted || visibleTabs[0]?.label !== "Informações") {
      setActiveTab(index);
    }
  };

  const verificaDocsConfirmados = async () => {
    try {
      const response =
        await blockchainService.getConfirmedDocumentsCount(
          project.id,
          headers
        );

      const { requirements_met, total_confirmed } =
        response?.data || response || {};

      const allRequirementsMet =
        requirements_met?.comercial &&
        requirements_met?.juridico &&
        requirements_met?.engenharia &&
        total_confirmed >= 9;

      setDocConfirmed(allRequirementsMet);

      return allRequirementsMet;
    } catch (error) {
      console.error(
        "❌ Erro ao verificar documentos confirmados:",
        error
      );
      setDocConfirmed(false);
      return false;
    }
  };

  const startProject = async () => {
    if (!localProject?.id) {
      Swal.fire({
        title: "Erro!",
        text: "Projeto inválido. Recarregue a página e tente novamente.",
        icon: "error",
        confirmButtonText: "OK",
      });
      return;
    }

    try {
      // 1) Libera abas: só status (não reenviar owner/title — listagem usa owner_id
      //    e owner objeto quebra PrimaryKeyRelatedField no PATCH)
      await projectService.updateProject(localProject.id, {
        status: "started",
      });

      // 2) Casca de engenharia (OneToOne) — get-or-create; falha aqui não
      //    deve impedir o processo se o status já foi gravado
      try {
        await projectService.ensureEngineeringRecord(localProject.id);
      } catch (engError) {
        console.warn(
          "Status atualizado, mas falhou ao garantir registro de engenharia:",
          engError
        );
      }

      const updated = { ...localProject, status: "started" };
      setLocalProject(updated);
      onProjectUpdated?.(updated);

      Swal.fire({
        title: "Sucesso!",
        text: "O projeto foi inicializado com sucesso!",
        icon: "success",
        confirmButtonText: "OK",
      });
    } catch (error) {
      console.error("Erro ao iniciar o projeto:", error);
      Swal.fire({
        title: "Erro!",
        text: `Não foi possível inicializar o projeto. ${formatApiError(error)}`,
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  };

  // 🔥 CORRIGIDO: Navegação sem app_status
  const handleComeBack = () => {
    navigate("/welcome"); // Volta para a página inicial
  };

  LinearTabs.defaultProps = {
    collapsed: true,
  };

  // Uma única aba (ex.: PROP/GUARDIAN): não exibe a barra — evita revelar que
  // existem outras seções do projeto.
  const hideTabBar = visibleTabs.length <= 1;

  return (
    <TabContainer collapsed={collapsed}>
      {!hideTabBar ? (
        <TabsScrollWrapper status={tabStatus}>
          <LinearTabs collapsed={collapsed} status={tabStatus}>
            {visibleTabs.map((tab, index) => (
              <Tab
                key={tab.label}
                active={activeTab === index}
                onClick={() => handleTabClick(index)}
                status={tabStatus}
              >
                {tab.label}
              </Tab>
            ))}
          </LinearTabs>
        </TabsScrollWrapper>
      ) : null}

      {showAdministrativoWarning && (
        <WarningContainer>
          <AdministrativoCard />
        </WarningContainer>
      )}

      {showWarning && (
        <WarningContainer>
          <WarningCard />
        </WarningContainer>
      )}

      <Content collapsed={collapsed}>
        <AnimatePresence mode="wait">
          <motion.div
            key={visibleTabs[activeTab]?.label || activeTab}
            initial={pageMotion.initial}
            animate={pageMotion.animate}
            exit={pageMotion.exit}
            transition={pageMotion.transition}
            style={{ width: "100%" }}
          >
            {visibleTabs[activeTab]?.content}
          </motion.div>
        </AnimatePresence>
      </Content>

      <div style={{ padding: "0 1.5rem 2.5rem" }}>
        {activeTab === 0 && localProject && (
          <ButtonContainer>
            <StyledButton
              onClick={handleComeBack}
              style={{ margin: "0px 10px 0px 0px" }}
            >
              Voltar
            </StyledButton>

            <StyledButton
              onClick={handleRegister}
              style={{ margin: "0px 10px 0px 0px" }}
            >
              Editar Informações
            </StyledButton>

            {isNotStarted &&
              (currentUser?.user_type === "ADM" ||
                currentUser?.user_type === "ADMIN") && (
                <InitializeButton onClick={startProject}>
                  Inicializar Processo
                </InitializeButton>
              )}
          </ButtonContainer>
        )}
      </div>
    </TabContainer>
  );
};

export default ProjectTabs;